import React from 'react';
import { PermissionGroup, Permission } from '../types';
import styles from '../../../../styles/PermissionManagement.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';

interface PermissionGroupCardProps {
  group: PermissionGroup;
  allPermissions: Permission[];
  onEdit: (group: PermissionGroup) => void;
  onDelete: (groupId: string, groupName: string) => void;
  onToggleStatus: (groupId: string, currentStatus: boolean) => void;
  onTogglePermission: (groupId: string, permissionId: string, isCurrentlyActive: boolean) => void;
}

const PermissionGroupCard: React.FC<PermissionGroupCardProps> = ({
  group,
  allPermissions,
  onEdit,
  onDelete,
  onToggleStatus,
  onTogglePermission
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getModuleDisplay = (module: string) => {
    return module.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const activePermissionIds = new Set(group.permissions.map(p => p.id));

  const groupedAllPermissions = allPermissions.reduce((acc, perm) => {
    const module = perm.module_display || getModuleDisplay(perm.module);
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  const getGroupColor = (groupName: string) => {
    const colors = [
      { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', icon: '#764ba2' },
      { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', icon: '#f5576c' },
      { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', icon: '#00f2fe' },
      { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', icon: '#38f9d7' },
      { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', icon: '#fee140' },
      { bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', icon: '#330867' },
    ];
    const index = groupName.length % colors.length;
    return colors[index];
  };

  const groupColor = getGroupColor(group.name);

  const getGroupType = (groupName: string) => {
    const name = groupName.toLowerCase();
    if (name.includes('top level') || name.includes('full access')) return 'Top Level';
    if (name.includes('hod')) return 'HOD';
    if (name.includes('standard')) return 'Standard';
    return 'Custom';
  };

  const groupType = getGroupType(group.name);

  return (
    <div className={styles.permissionGroupCard}>
      <span className={styles.sectionBadge} style={{ 
        background: group.is_active ? 'var(--primary)' : 'var(--gray)'
      }}>
        {groupType}
      </span>
      
      <div className={styles.cardHeader}>
        <h3 className={styles.sectionTitle}>
          <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          {group.name}
        </h3>
        <p className={styles.cardDescription}>{group.description || 'No description provided'}</p>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.cardStats}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{group.permissions_count}</div>
            <div className={styles.statLabel}>Permissions</div>
          </div>
          <div className={styles.statDivider}></div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{Object.keys(groupedAllPermissions).length}</div>
            <div className={styles.statLabel}>Modules</div>
          </div>
          <div className={styles.statDivider}></div>
          <div className={styles.statItem}>
            <label className={styles.toggleSwitch}>
              <input 
                type="checkbox" 
                checked={group.is_active}
                onChange={() => onToggleStatus(group.id, group.is_active)}
              />
              <span className={styles.toggleSlider}></span>
            </label>
            <div className={styles.statLabel}>Status</div>
          </div>
        </div>

        <div className={styles.permissionsPreview}>
          {Object.keys(groupedAllPermissions).length > 0 ? (
            <div className={styles.allModulesList}>
              {Object.entries(groupedAllPermissions).map(([module, permissions]) => (
                <div key={module} className={styles.permissionModule}>
                  <div className={styles.moduleHeader}>
                    <span className={styles.moduleName}>{module}</span>
                    <span className={styles.actionsCount}>
                      {permissions.filter(p => activePermissionIds.has(p.id)).length}/{permissions.length}
                    </span>
                  </div>
                  <div className={styles.actionsList}>
                    {permissions.map((permission) => {
                      const isActive = activePermissionIds.has(permission.id);
                      return (
                        <span
                          key={permission.id}
                          className={`${styles.actionBadge} ${isActive ? styles.actionBadgeActive : styles.actionBadgeInactive}`}
                          onClick={() => onTogglePermission(group.id, permission.id, isActive)}
                          title={`Click to ${isActive ? 'remove' : 'add'} ${permission.action_display || permission.action} permission`}
                        >
                          {permission.action_display || permission.action}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.noPermissions}>No permissions available</p>
          )}
        </div>

        <div className={styles.cardFooter}>
          <div className={styles.cardMetadata}>
            <span className={styles.metadataItem}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              {formatDate(group.created_at)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionGroupCard;

